import Title from '@cohort/components-xps/components/Title';
import {classNames} from '@cohort/shared-frontend/utils/classNames';
import useSpaceContext from '@cohort/wallet/hooks/useSpaceContext';
import useThemeContext from '@cohort/wallet/hooks/useThemeContext';
import {Flag, Gift, Stack} from '@phosphor-icons/react';
import type {FC} from 'react';
import {Fragment} from 'react';
import {useTranslation} from 'react-i18next';

interface AchievementCardProps {
  icon: JSX.Element;
  counter: number;
  text: string;
}

const AchievementCard: FC<AchievementCardProps> = ({icon, counter, text}) => {
  const {hasDarkBg} = useThemeContext();

  return (
    <div
      className={classNames(
        'rounded-b-xl rounded-l-xl rounded-r-xl rounded-t-xl p-4 md:h-full',
        hasDarkBg ? 'bg-white/5 text-white/60' : 'bg-black/4 text-black/60'
      )}
    >
      <div className="flex gap-x-2">
        <div className="my-auto">{icon}</div>
        <span className="text-xl font-semibold">{counter}</span>
      </div>
      <div className="flex flex-col">
        <span className="text-sm">{text}</span>
      </div>
    </div>
  );
};

export const Achievements: FC = () => {
  const {space} = useSpaceContext();
  const {t} = useTranslation('components', {keyPrefix: 'space.achievements'});
  const {accentColor} = useThemeContext();

  if (space === undefined) {
    return <Fragment />; // Replace me by a skeleton loader.
  }

  const {digitalAssetCount, perks, journeys} = space;
  const activeJourneysCount = journeys.filter(
    campaign =>
      campaign.journey.participation !== null && campaign.journey.participation.completedAt === null
  ).length;

  const achievementCards = [
    {
      icon: <Flag size={20} style={{color: accentColor}} />,
      text: t('activeJourneys', {
        count: activeJourneysCount,
      }),
      counter: activeJourneysCount,
      key: 'journeys',
    },
    {
      icon: <Stack size={20} style={{color: accentColor}} />,
      text: t('digitalAssets', {count: digitalAssetCount ?? 0}),
      counter: digitalAssetCount ?? 0,
      key: 'digitalAssets',
    },
    {
      icon: <Gift size={20} style={{color: accentColor}} />,
      text: t('activePerks', {count: perks.length}),
      counter: perks.length,
      key: 'activePerks',
    },
  ];

  return (
    <div className="flex w-full flex-col gap-4">
      <Title classes="text-2xl" content={t('title')} />
      <div
        className="grid w-full gap-2 md:gap-1 lg:gap-2"
        style={{
          gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))',
        }}
      >
        {achievementCards.map(achievementCard => (
          <AchievementCard
            key={achievementCard.key}
            icon={achievementCard.icon}
            text={achievementCard.text}
            counter={achievementCard.counter}
          />
        ))}
      </div>
    </div>
  );
};
